// response-render.core.ts
import { RenderKind, RenderRequest, RenderResponse } from './response-render.types';

export function renderResponse(req: RenderRequest): RenderResponse {
  try {
    const raw = (req.text ?? '').toString();
    const { text, truncated } = truncate(raw, req.maxChars);

    const pretty = tryPrettyBody(text);
    const srcText = pretty.text ?? '';
    const lineCount = srcText ? countLines(srcText) : 0;

    const q = (req.query ?? '').toString().trim();
    const idx = Number.isFinite(req.index) ? Math.max(0, Math.floor(req.index)) : 0;

    if (!q || q.length < 2) {
      const escaped = escapeHtml(srcText);
      return {
        id: req.id,
        html: wrapAsLines(escaped),
        matches: 0,
        index: 0,
        lineCount,
        kind: pretty.kind,
        truncated,
      };
    }

    const res = highlightText(srcText, q, idx, req.maxMatches);

    return {
      id: req.id,
      html: wrapAsLines(res.html),
      matches: res.matches,
      index: res.clampedIndex,
      lineCount,
      kind: pretty.kind,
      truncated,
    };
  } catch (e: any) {
    return {
      id: req.id,
      html: '',
      matches: 0,
      index: 0,
      lineCount: 1,
      kind: 'text',
      truncated: false,
      error: e?.message ?? String(e),
    };
  }
}

/* --- helpers (copy from your worker) --- */

function truncate(text: string, maxChars: number): { text: string; truncated: boolean } {
  const max = Number.isFinite(maxChars) ? Math.max(0, Math.floor(maxChars)) : 0;
  if (!max || text.length <= max) return { text, truncated: false };
  return {
    text: text.slice(0, max) + `\n\n… truncated (${text.length} chars total)`,
    truncated: true,
  };
}

function tryPrettyBody(text: string): { text: string; kind: RenderKind } {
  const t = (text ?? '').toString();
  const trimmed = t.trim();
  if (!trimmed) return { text: '', kind: 'text' };

  if (isJsonLike(trimmed)) {
    try {
      return { text: JSON.stringify(JSON.parse(trimmed), null, 2), kind: 'json' };
    } catch { /* noop */ }
  }

  if (isXmlLike(trimmed)) {
    try {
      return { text: prettyXmlFallback(trimmed), kind: 'xml' };
    } catch { /* noop */ }
  }

  return { text: t, kind: 'text' };
}

function isJsonLike(trimmed: string): boolean {
  return (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));
}

function isXmlLike(trimmed: string): boolean {
  return trimmed.startsWith('<') && trimmed.endsWith('>');
}

function prettyXmlFallback(xml: string): string {
  const PADDING = '  ';
  const reg = /(>)(<)(\/*)/g;
  const formatted = xml.replace(reg, '$1\n$2$3');
  let pad = 0;
  return formatted
    .split('\n')
    .map(line => {
      const l = line.trim();
      if (l.match(/^<\/.+>/)) pad = Math.max(pad - 1, 0);
      const out = PADDING.repeat(pad) + l;
      if (l.match(/^<[^!?/].*[^/]>$/)) pad += 1;
      return out;
    })
    .join('\n');
}

function countLines(text: string): number {
  if (!text) return 1;
  let n = 1;
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) === 10) n++;
  return n;
}

function escapeHtml(s: string): string {
  return (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function wrapAsLines(html: string): string {
  const lines = (html ?? '').split('\n');
  return lines.map(l => `<div class="it-resline">${l.length ? l : '&nbsp;'}</div>`).join('');
}

function highlightText(
  source: string,
  query: string,
  activeIndex: number,
  maxMatches: number
): { html: string; matches: number; clampedIndex: number } {
  const src = (source ?? '').toString();
  const q = (query ?? '').toString();
  if (!src || !q) return { html: escapeHtml(src), matches: 0, clampedIndex: 0 };

  const hay = src.toLowerCase();
  const needle = q.toLowerCase();
  const limit = Number.isFinite(maxMatches) ? Math.max(0, Math.floor(maxMatches)) : 2000;

  const parts: string[] = [];
  let pos = 0;
  let count = 0;

  while (count < limit) {
    const hit = hay.indexOf(needle, pos);
    if (hit === -1) break;

    parts.push(escapeHtml(src.slice(pos, hit)));

    const matchText = src.slice(hit, hit + q.length);
    parts.push(`<mark class="it-mark" data-mi="${count}">${escapeHtml(matchText)}</mark>`);

    pos = hit + q.length;
    count++;
  }

  parts.push(escapeHtml(src.slice(pos)));

  const matches = count;
  const clampedIndex = matches ? Math.max(0, Math.min(activeIndex, matches - 1)) : 0;

  let joined = parts.join('');

  if (matches) {
    const needleMark = `<mark class="it-mark" data-mi="${clampedIndex}">`;
    const activeMark = `<mark class="it-mark is-active" data-mi="${clampedIndex}">`;
    joined = joined.replace(needleMark, activeMark);
  }

  return { html: joined, matches, clampedIndex };
}
